// Mirrors functions/src/utils/normalizeCollateral.ts's EnrichBoatInfoInput —
// this is the wire shape sent as the enrichLendAPIApplication callable's
// `boatInfo` field. Keep the two definitions in sync if either changes.
export interface EnrichBoatInfoInput {
  type?: string;
  year?: number | string;
  make?: string;
  model?: string;
  length?: number | string;
  engineYear?: number | string;
  engineMake?: string;
  engineModel?: string;
  engineHorsepower?: number | string;
  // validMake is intentionally never used to relax matching thresholds on the
  // backend — see the matching comment in normalizeCollateral.ts. Present here
  // only because it's part of the payload shape decoded off the listing.
  validMake?: string;
  validModel?: string;
  // Engine drive/configuration category from the BoatTrader listing API
  // (e.g. 'inboard', 'outboard', 'inboard-outboard'). Passed through to
  // Salesforce Style__c mapping (TRIDENT-1122).
  engineCategory?: string;
}

export interface ExtractedEnrichmentPayload {
  boatInfo: EnrichBoatInfoInput | null;
  source: number | null;
}

/**
 * Decodes the `boatInfo` and `source` fields directly off a LendAPI postMessage
 * payload's own custom querystring param — the SAME payload the calling hook
 * already receives — rather than round-tripping through the
 * `prequal-{imtId}` localStorage record.
 *
 * `payload.app_url_params?.custom` (falling back to `payload.custom`) arrives
 * as a FLAT, single-encoding-layer query string: top-level keys/values are
 * delimited by raw `&`/`=`, and the nested `boatInfo` value is itself a
 * percent-encoded JSON string (so any `&`, `"`, `{`, etc. inside boatInfo's
 * own field values — e.g. a seller name like "Harborview Boat & Yacht Sales"
 * — are safely escaped as `%26`/`%22`/`%7B` and never collide with the
 * top-level `&` delimiters). `URLSearchParams` must be constructed directly
 * from that raw string — pre-decoding it with `decodeURIComponent` first would
 * unmask those escaped delimiters (turning `%26` into a literal `&`) BEFORE
 * `URLSearchParams` gets to split on real delimiters, corrupting/truncating
 * the boatInfo JSON. `URLSearchParams#get` already fully percent-decodes each
 * value in isolation, so `boatInfo` needs no further `decodeURIComponent` pass
 * before `JSON.parse`.
 *
 * Never throws — returns { boatInfo: null, source: null } if the payload, its
 * custom param, or any decode/parse step is absent or invalid.
 */
export function extractBoatInfoAndSourceFromPayload(payload: any): ExtractedEnrichmentPayload {
  const empty: ExtractedEnrichmentPayload = { boatInfo: null, source: null };

  const encodedCustomParam: string | undefined = payload?.app_url_params?.custom || payload?.custom;
  if (!encodedCustomParam) {
    return empty;
  }

  let flatQuery: URLSearchParams;
  try {
    flatQuery = new URLSearchParams(encodedCustomParam);
  } catch (error) {
    console.error('[LendAPI Enrichment] Error decoding custom param:', error);
    return empty;
  }

  let boatInfo: EnrichBoatInfoInput | null = null;
  const boatInfoParam = flatQuery.get('boatInfo');
  if (boatInfoParam) {
    try {
      boatInfo = JSON.parse(boatInfoParam);
    } catch (error) {
      console.error('[LendAPI Enrichment] Error parsing boatInfo:', error);
      boatInfo = null;
    }
  }

  let source: number | null = null;
  const sourceParam = flatQuery.get('source');
  if (sourceParam) {
    const parsed = parseInt(sourceParam, 10);
    source = Number.isNaN(parsed) ? null : parsed;
  }

  return { boatInfo, source };
}
